import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLicense } from '../contexts/LicenseContext';
import LoginPage from './LoginPage';
import SetPasswordPage from './SetPasswordPage';
import RegisterPage from '../scenes/auth/RegisterPage';
import LicenseBlockedPage from './LicenseBlockedPage';
import { Box, CircularProgress, Typography } from '@mui/material';

const ProtectedRoute = ({ children, adminRequired = false, superAdminRequired = false, operatorRequired = false }) => {
  const { user, loading, isAuthenticated, isAdmin, isSuperAdmin, isOperator } = useAuth();
  const { licenseBlocked, loading: licenseLoading } = useLicense();
  const location = useLocation();

  // Check for public auth routes (password set/reset, registration)
  const isSetPasswordRoute = location.pathname === '/auth/set-password' ||
                             location.pathname === '/set-password';
  const isRegisterRoute = location.pathname === '/register';

  // Set-password and register are reachable for ANY visitor — including
  // someone already logged in as a different user. Previously these
  // pages only rendered when {@code !isAuthenticated}, which is why
  // operators with an existing session who clicked an invite link
  // ended up on a blank page: ProtectedRoute fell through to
  // {@code children} and no app route was registered for the path.
  // Short-circuit before the auth gate so the public auth flow always
  // gets its own dedicated page render.
  if (isSetPasswordRoute) {
    return <SetPasswordPage />;
  }
  if (isRegisterRoute) {
    return <RegisterPage />;
  }

  // Show loading for auth check first
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Only wait for license loading after user is authenticated
  if (licenseLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Check if license is blocked (after authentication)
  if (licenseBlocked) {
    return <LicenseBlockedPage />;
  }

  if (superAdminRequired && !isSuperAdmin) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
        }}
      >
        <Typography variant="h4" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You don't have permission to view this page.
        </Typography>
      </Box>
    );
  }

  // Operator surface — accepts SUPER_ADMIN OR WEB_ADMIN. Used by the
  // /operator/* tree which is shared between full operators and the
  // website-only sub-operator. Within the operator panel itself, the
  // sidebar trims Platform Controls for WEB_ADMIN.
  if (operatorRequired && !isOperator) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
        }}
      >
        <Typography variant="h4" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You don't have permission to view this page.
        </Typography>
      </Box>
    );
  }

  if (adminRequired && !isAdmin) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
        }}
      >
        <Typography variant="h4" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You don't have permission to view this page.
        </Typography>
      </Box>
    );
  }

  return children;
};

export default ProtectedRoute;